/**
 * MassCreateDialog — "we are delivering 20 trucks today, no times" (#66).
 *
 * ⭐ THE SCENARIO, in Pete's words:
 *
 *   "This is a client/carrier saying we are delivering 20 trucks today and
 *    Symbia is saying we are not making you set dedicated appointment times.
 *    Symbia will have 2 dock doors dedicated for the client/carrier but the
 *    Symbia scheduler will mass create 20 appointments with no container/PO/BOL/
 *    Reference numbers and no appointment times. The appointments will be
 *    Unassigned (time and dock door) and then be assigned a driver and a dock
 *    door when the truck arrives."
 *
 * So this form asks for FIVE things — day, direction, client, carrier, how many
 * — and nothing else. Every field the ordinary New Appointment form has is
 * deliberately absent: a door, a time, a PO, a BOL, a reference number. None of
 * them are known yet, and a form that asks for them invites a placeholder that
 * later reads as a fact somebody established.
 *
 * ⛔ THE DIALOG DOES NOT CLOSE ON SUCCESS. It switches to a RESULT view holding
 * the batch, its warnings and an Undo. Twenty appointments is a big enough act
 * that "did that work, and can I take it back?" needs an answer that stays on
 * screen — a 4-second toast (Calendar's own) is not one, and an Undo the
 * scheduler has to race is worse than no Undo at all.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, Undo2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError,
    massCreateAppointments,
    backOutMassCreateBatch,
    type MassCreateResult,
    type BatchBackoutResult,
} from '@/api/client';
import type { LookupsDto } from '@/api/types';

/** Matches MAX_BATCH in src/lib/massCreatePlan.ts. */
const MAX_BATCH = 100;

interface MassCreateDialogProps {
    open: boolean;
    onClose: () => void;
    facilityCode: string;
    /** The day the calendar is showing — the sensible default for "today". */
    localDate: string;
    lookups: LookupsDto;
    /** Fired after a create OR an undo, so the caller can reload the grid. */
    onChanged: () => void;
    getToken: () => Promise<string>;
}

export function MassCreateDialog({
    open,
    onClose,
    facilityCode,
    localDate,
    lookups,
    onChanged,
    getToken,
}: MassCreateDialogProps) {
    const [direction, setDirection] = useState<'INBOUND' | 'OUTBOUND'>('INBOUND');
    const [date, setDate] = useState(localDate);
    const [clientCode, setClientCode] = useState('');
    const [carrierCode, setCarrierCode] = useState('');
    // ⚠️ A STRING, NOT A NUMBER. A numeric state makes an empty field become 0,
    // and "0" is a value the form then has to pretend it did not see. Parsed once,
    // at the edge, where the failure is visible.
    const [countText, setCountText] = useState('');
    const [comments, setComments] = useState('');

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /**
     * ⭐ THE DAILY-CAP SENTENCE FROM THE SERVER, held separately from `error`.
     * The distinction is the whole override flow: an `error` is something the
     * scheduler must fix, and this is something they may knowingly proceed past.
     * Rendering both the same way would make one of them a lie.
     */
    const [limitWarning, setLimitWarning] = useState<string | null>(null);
    const [result, setResult] = useState<MassCreateResult | null>(null);
    const [undone, setUndone] = useState<BatchBackoutResult | null>(null);

    // Every open starts clean. A batch id or a stale warning surviving into the
    // next use is how somebody undoes yesterday's twenty trucks.
    useEffect(() => {
        if (!open) return;
        setDirection('INBOUND');
        setDate(localDate);
        setClientCode('');
        setCarrierCode('');
        setCountText('');
        setComments('');
        setBusy(false);
        setError(null);
        setLimitWarning(null);
        setResult(null);
        setUndone(null);
    }, [open, localDate]);

    const count = useMemo(() => {
        const n = Number(countText.trim());
        return Number.isInteger(n) && n >= 1 && n <= MAX_BATCH ? n : null;
    }, [countText]);

    const countProblem = useMemo(() => {
        const raw = countText.trim();
        if (!raw) return null;                       // not yet an error, just empty
        const n = Number(raw);
        if (!Number.isFinite(n)) return 'Enter a number.';
        if (!Number.isInteger(n)) return 'Whole trucks only.';
        if (n < 1) return 'At least one.';
        // ⚠️ "200" instead of "20" is one keystroke, and undoing 200 rows is a
        // worse afternoon than re-entering a number.
        if (n > MAX_BATCH) return `${MAX_BATCH} at a time is the most. Split it, or check the number.`;
        return null;
    }, [countText]);

    /**
     * ⛔ THE CARRIER IS NO LONGER REQUIRED (2026-08-24, arch §1R.8).
     *
     * Pete, blocked on a TRAFFIX load: *"I think you should be able to mass create
     * appointments just like the Upload (both without carriers) correct? Kinda weird
     * to have one with the restriction and other not."*
     *
     * ⚠️ A BROKERED LOAD GENUINELY HAS NO CARRIER WHEN THE SCHEDULE IS MADE — Traffix
     * is the broker and the carrier is tendered afterwards. The upload path has always
     * accepted that and written 'TBD'; requiring it here made the same freight
     * creatable one way and impossible the other (§1R.5).
     */
    const canSubmit = !!clientCode && count !== null && !!date && !busy;

    const clientName = lookups.clients.find(c => c.ClientCode === clientCode)?.ClientName ?? null;
    const carrierName = lookups.carriers.find(c => c.CarrierCode === carrierCode)?.CarrierName ?? null;

    /**
     * @param acknowledgeDailyLimit ⛔ Only ever true because the scheduler
     * clicked the button that says so. Never a default, never remembered across
     * opens — the cap exists so other customers can still book.
     */
    async function submit(acknowledgeDailyLimit: boolean) {
        if (count === null) return;
        setBusy(true);
        setError(null);
        if (!acknowledgeDailyLimit) setLimitWarning(null);
        try {
            const token = await getToken();
            const r = await massCreateAppointments(token, {
                facilityCode,
                localDate: date,
                direction,
                clientCode,
                clientName,
                carrierCode,
                carrierName,
                count,
                comments: comments.trim() || null,
                acknowledgeDailyLimit,
            });
            setResult(r);
            setLimitWarning(null);
            onChanged();
        } catch (err) {
            // ⛔ MATCH ON `code`, NEVER ON THE MESSAGE TEXT. Two different 409s
            // arrive here — the daily cap, which is offerable as an override, and
            // anything else, which is not. Matching on wording breaks the first
            // time somebody improves a sentence (see ApiError.code).
            if (err instanceof ApiCallError && err.details.code === 'daily_limit_reached') {
                setLimitWarning(err.details.error);
            } else {
                setError(formatErr(err));
            }
        } finally {
            setBusy(false);
        }
    }

    async function undo() {
        if (!result) return;
        setBusy(true);
        setError(null);
        try {
            const token = await getToken();
            /**
             * ⛔ `date`, NOT THE `localDate` PROP. The scheduler may have created the
             * batch for tomorrow while the calendar still shows today; sending the
             * calendar's day would have the server refuse with "that batch is for
             * 2026-08-25" on a batch created three seconds ago.
             */
            setUndone(await backOutMassCreateBatch(token, result.batchId, facilityCode, date));
            onChanged();
        } catch (err) {
            setError(formatErr(err));
        } finally {
            setBusy(false);
        }
    }

    // -----------------------------------------------------------------------
    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {result ? 'Appointments created' : 'Mass create appointments'}
                    </DialogTitle>
                    <DialogDescription>
                        {result
                            ? 'No times and no doors — assign each one when its truck arrives.'
                            : 'For a client delivering a known number of trucks on a day, with no '
                              + 'agreed times. Each appointment gets its time and door when the '
                              + 'truck actually arrives.'}
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                        <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                {/* ---------------- RESULT VIEW ---------------- */}
                {result ? (
                    <div className="space-y-3">
                        <div className="flex gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 mt-0.5" />
                            <div>
                                <div className="font-medium">
                                    {result.created} appointment{result.created === 1 ? '' : 's'} for{' '}
                                    {clientName ?? clientCode} on {date}.
                                </div>
                                <div className="text-muted-foreground mt-0.5">
                                    Unassigned — no time, no door. They appear in the unassigned
                                    list and count against the day's appointment limit, so nobody
                                    else can book over them.
                                </div>
                            </div>
                        </div>

                        {/* ⭐ SHOWN, NOT SWALLOWED. "That day is a holiday" is worth
                            knowing just as much after twenty rows exist as before. */}
                        {result.warnings.length > 0 && (
                            <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                                <ul className="space-y-1">
                                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                            </div>
                        )}

                        {undone && (
                            <div className="flex gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
                                <Undo2 className="h-4 w-4 shrink-0 mt-0.5" />
                                {/* ⛔ THE SERVER'S OWN SENTENCE, which accounts for every
                                    appointment — removed, cancelled AND left alone. A
                                    scheduler who presses Undo and is told "removed 20"
                                    when three trucks were already on doors has been
                                    misled about the state of his own floor. */}
                                <div className="space-y-1">
                                    <span>{undone.message}</span>
                                    {/* ⚠️ THE REFUSALS ARE LISTED, NOT COUNTED. "left 3
                                        alone" is not actionable; the appointment ids and
                                        the reasons are. */}
                                    {undone.kept.length > 0 && (
                                        <ul className="text-xs text-muted-foreground space-y-0.5">
                                            {undone.kept.map((k) => (
                                                <li key={k.appointmentId}>
                                                    #{k.appointmentId} — {k.reason}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <div className="flex w-full justify-between gap-2">
                                {!undone ? (
                                    <Button
                                        variant="outline" size="sm" onClick={() => void undo()}
                                        disabled={busy}
                                    >
                                        {busy
                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            : <Undo2 className="h-3.5 w-3.5" />}
                                        Undo this batch
                                    </Button>
                                ) : <span />}
                                <Button size="sm" onClick={onClose} disabled={busy}>Done</Button>
                            </div>
                        </DialogFooter>
                    </div>
                ) : (
                /* ---------------- FORM VIEW ---------------- */
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="mc-date">Day</Label>
                            <Input
                                id="mc-date" type="date" value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Direction</Label>
                            <Select
                                value={direction}
                                onValueChange={(v) => setDirection(v as 'INBOUND' | 'OUTBOUND')}
                            >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="INBOUND">Inbound</SelectItem>
                                    <SelectItem value="OUTBOUND">Outbound</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label>Client</Label>
                        <Select value={clientCode} onValueChange={setClientCode}>
                            <SelectTrigger><SelectValue placeholder="Choose a client" /></SelectTrigger>
                            <SelectContent>
                                {lookups.clients.map(c => (
                                    <SelectItem key={c.ClientCode} value={c.ClientCode}>
                                        {c.ClientName || c.ClientCode}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label>Carrier (optional)</Label>
                        <Select value={carrierCode} onValueChange={setCarrierCode}>
                            <SelectTrigger><SelectValue placeholder="Not known yet / brokered" /></SelectTrigger>
                            <SelectContent>
                                {lookups.carriers.map(c => (
                                    <SelectItem key={c.CarrierCode} value={c.CarrierCode}>
                                        {c.CarrierName || c.CarrierCode}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {/* ⛔ THE CONSEQUENCE, STATED — not engineered away (§1R.8).
                            A brokered load has no carrier when the schedule is made, so
                            'TBD' is the honest value. What is lost is real and the
                            scheduler should know it, in the same words the upload
                            screen uses. */}
                        <p className="text-muted-foreground text-xs">
                            {carrierCode
                                ? 'Carrier-scoped limits and door dedications will apply.'
                                : 'Leave this blank for a brokered load — the carrier is often '
                                  + 'tendered after the schedule goes out. The appointments are '
                                  + 'created either way, but they will not appear in a carrier '
                                  + 'portal and carrier-specific limits will not apply.'}
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="mc-count">How many trucks</Label>
                        <Input
                            id="mc-count" inputMode="numeric" value={countText}
                            placeholder="e.g. 20"
                            onChange={(e) => setCountText(e.target.value)}
                            className={countProblem ? 'border-destructive' : undefined}
                        />
                        {countProblem
                            ? <p className="text-xs text-destructive">{countProblem}</p>
                            : <p className="text-xs text-muted-foreground">
                                  No PO, BOL, container or reference numbers — the paperwork
                                  arrives with the truck.
                              </p>}
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="mc-comments">Note (optional)</Label>
                        <Textarea
                            id="mc-comments" rows={2} value={comments}
                            placeholder="e.g. 2 doors dedicated, driver checks in at the guard shack"
                            onChange={(e) => setComments(e.target.value)}
                        />
                    </div>

                    {/* ⭐ THE OVERRIDE. Shown only when the server has actually
                        refused, carrying the server's own sentence — which names the
                        number, the scope, the date and the band hours. Re-stating it
                        in the UI would give two versions of one rule to keep in
                        step. */}
                    {limitWarning && (
                        <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                            <div>
                                <div className="font-medium">This crosses the day's limit</div>
                                <div className="mt-0.5">{limitWarning}</div>
                                <div className="text-muted-foreground mt-1.5 text-xs">
                                    The limit exists so other customers can still book. Create
                                    anyway only if Symbia has agreed to these trucks.
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <div className="flex w-full justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
                                Cancel
                            </Button>
                            {limitWarning ? (
                                /* ⛔ A SEPARATE BUTTON, not a checkbox the scheduler
                                   might have left ticked from last time. The
                                   acknowledgement is this click and nothing else. */
                                <Button
                                    variant="destructive" size="sm"
                                    onClick={() => void submit(true)} disabled={busy}
                                >
                                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                    Create anyway
                                </Button>
                            ) : (
                                <Button
                                    size="sm" onClick={() => void submit(false)} disabled={!canSubmit}
                                >
                                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                    Create {count ?? ''} appointment{count === 1 ? '' : 's'}
                                </Button>
                            )}
                        </div>
                    </DialogFooter>
                </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function formatErr(err: unknown): string {
    if (err instanceof ApiCallError) return err.details.error;
    return err instanceof Error ? err.message : String(err);
}
